import {GeneratedVideoChunk} from '../../../data/models/LibraryFile/GeneratedVideoChunk';
import {MicroreelVideoSource} from './microreelVideoSources';

// Persistent chunk→reel assignment for microreel scope editing.
//
// The editor used to re-derive which chunks belong to the reel from the
// reel's time window on every load, but edits (narration, trims) change the
// chunk durations: after a reload the window no longer matches the timeline
// and every lock shifts one reel over. Chunk ids are stable across edits, so
// the assignment is computed once — while the timings still line up — and
// persisted per source video. Chunks created later (replacing media splits a
// chunk into a child with a new id) are appended to the reel being edited,
// since locked chunks cannot produce children.

export interface MicroreelChunkMap {
  // chunkId → reel (lesson) id
  byChunk: {[chunkId: string]: string};
}

export interface ReelSourceWindows {
  reelId: string;
  sources: MicroreelVideoSource[];
}

const STORAGE_KEY = 'microreelChunkMap';

export const readChunkMap = (
  mediaFileId?: string,
): MicroreelChunkMap | undefined => {
  if (!mediaFileId) return undefined;
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY}:${mediaFileId}`);
    return raw ? (JSON.parse(raw) as MicroreelChunkMap) : undefined;
  } catch {
    return undefined;
  }
};

export const storeChunkMap = (mediaFileId: string, map: MicroreelChunkMap) => {
  try {
    localStorage.setItem(`${STORAGE_KEY}:${mediaFileId}`, JSON.stringify(map));
  } catch {
    // Storage unavailable: the editor falls back to the window heuristic.
  }
};

const toPath = (url?: string) => {
  if (!url) return '';
  const noQuery = url.split('?')[0];
  try {
    return new URL(noQuery).pathname.toLowerCase();
  } catch {
    return noQuery.toLowerCase();
  }
};

export const buildChunkMap = (
  chunks: GeneratedVideoChunk[],
  reels: ReelSourceWindows[],
): MicroreelChunkMap | undefined => {
  const included = [...chunks]
    .filter(chunk => chunk.include)
    .sort((a, b) => a.order - b.order);
  if (!included.length || !reels.length) return undefined;
  const byChunk: {[chunkId: string]: string} = {};

  // One scene per reel: microreel generation creates exactly one chunk per
  // reel, and a count match survives any duration drift from later edits.
  if (included.length === reels.length) {
    included.forEach((chunk, index) => {
      byChunk[chunk.id] = reels[index].reelId;
    });
    return {byChunk};
  }

  // Reel contents that reference chunk files directly: match by URL.
  const urlToReel = new Map<string, string>();
  reels.forEach(reel => {
    reel.sources.forEach(source => {
      const path = toPath(source.url);
      if (path && !urlToReel.has(path)) {
        urlToReel.set(path, reel.reelId);
      }
    });
  });
  chunks.forEach(chunk => {
    const urls = [
      toPath(chunk.uri),
      ...(chunk.artifacts ?? []).flatMap(artifact => [
        toPath(artifact.uri),
        toPath(artifact.publicUri),
      ]),
    ];
    const owner = urls
      .map(url => (url ? urlToReel.get(url) : undefined))
      .find(Boolean);
    if (owner) {
      byChunk[chunk.id] = owner;
    }
  });
  if (Object.keys(byChunk).length) {
    return {byChunk};
  }

  // Single compiled source: assign by each chunk's center against the reel
  // windows (only trustworthy before any edit shifts the durations).
  const paths = new Set<string>();
  reels.forEach(reel =>
    reel.sources.forEach(source => {
      const path = toPath(source.url);
      if (path) paths.add(path);
    }),
  );
  if (paths.size === 1) {
    let cursor = 0;
    included.forEach(chunk => {
      const center = cursor + chunk.duration / 2;
      const owner = reels.find(reel => {
        if (!reel.sources.length) return false;
        const start = Math.min(
          ...reel.sources.map(source => source.startInSeconds),
        );
        const end = Math.max(
          ...reel.sources.map(source => source.endInSeconds),
        );
        return center >= start - 0.5 && center <= end + 0.5;
      });
      if (owner) {
        byChunk[chunk.id] = owner.reelId;
      }
      cursor += chunk.duration;
    });
  }

  return Object.keys(byChunk).length ? {byChunk} : undefined;
};
